/* eslint-disable */
/**
 * Phase 98q — Menu Tuiles (mosaic layout) — carte publique moderne.
 *
 * Routes :
 * - `/carte`               = catalogue global des plats visibles
 * - `/carte/:distId`       = plats affichés sur une machine précise
 *
 * Cible : QR code sur les machines → scanné depuis un téléphone → belle carte HTML
 * responsive au lieu du PDF classique. Mobile-first, animations subtiles, deep-link
 * vers la boutique en ligne pour commander.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Flame, ShoppingBag, MapPin, ArrowRight, Loader2, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const COLORS = {
  bg: "#050505",
  surface: "#0F0F0F",
  primary: "#F2ECE4",  // ivoire
  muted: "#8B8B85",
  gold: "#C1A063",
  accent: "#D97757",   // chili
  basil: "#88A066",
};

// Palette par catégorie (pour les chips filtres et bordures subtiles)
const CATEGORY_TONE = {
  Boeuf:   { bg: "#3A1F1F", label: "Bœuf" },
  Poulet:  { bg: "#332614", label: "Poulet" },
  Canard:  { bg: "#2A1F0F", label: "Canard" },
  Poisson: { bg: "#1F2A33", label: "Poisson" },
  Végé:    { bg: "#1F2E1F", label: "Végé" },
  Général: { bg: "#1F1F2A", label: "Général" },
  Starter: { bg: "#2A1F2A", label: "Starter" },
};

function resolvePhoto(url, w = 800) {
  if (!url) return null;
  // Route interne /files/… → on préfixe API + on demande une largeur
  if (url.startsWith("/files/") || url.startsWith("files/")) {
    const clean = url.startsWith("/") ? url : `/${url}`;
    return `${API}${clean}?w=${w}`;
  }
  return url;
}

export default function MenuTuiles() {
  const { distId } = useParams();
  const [products, setProducts] = useState([]);
  const [distributor, setDistributor] = useState(null);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCat, setActiveCat] = useState("Toutes");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // Récupère le logo + tagline via l'endpoint boutique-section
        try {
          const bs = await api.get("/shop/boutique-section");
          if (!cancelled) setLogo(bs.data?.logo || null);
        } catch { /* silencieux */ }

        if (distId) {
          const [dc, ddists] = await Promise.all([
            api.get(`/shop/distributors/${distId}/menu-card`),
            api.get(`/distributors`).catch(() => ({ data: [] })),
          ]);
          if (cancelled) return;
          setProducts(dc.data.products || []);
          const found = (ddists.data || []).find((d) => d.id === distId);
          setDistributor(found || dc.data.distributor || null);
        } else {
          const r = await api.get("/shop/menu-cards/catalog");
          if (cancelled) return;
          setProducts(r.data.products || []);
          setDistributor(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Impossible de charger la carte");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [distId]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.category && set.add(p.category));
    return ["Toutes", ...Array.from(set).sort()];
  }, [products]);

  const visible = useMemo(() => {
    // Dédup par SKU (un produit peut apparaître dans plusieurs slots d'une machine)
    const seen = new Set();
    const dedup = [];
    for (const p of products) {
      if (!p.sku || seen.has(p.sku)) continue;
      seen.add(p.sku);
      dedup.push(p);
    }
    if (activeCat === "Toutes") return dedup;
    return dedup.filter((p) => p.category === activeCat);
  }, [products, activeCat]);

  return (
    <div className="min-h-screen" style={{ background: COLORS.bg, color: COLORS.primary }} data-testid="menu-tuiles-page">
      {/* Header sombre avec logo + nom machine */}
      <header className="px-4 sm:px-6 lg:px-10 pt-6 pb-8 sm:pt-10 sm:pb-12 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            {/* Logo optionnel (Phase 98p CMS slug=logo-shop) */}
            {logo?.slug && (
              <img
                src={`${API}/api/shop/boutique-section/${logo.slug}/photo.jpg${logo.uploaded_at ? `?v=${encodeURIComponent(logo.uploaded_at)}` : ""}`}
                alt="Maison Thaï"
                className="mb-3 max-h-14 sm:max-h-16 object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="menu-logo"
              />
            )}
            <h1
              className="font-heading text-4xl sm:text-6xl md:text-7xl leading-none tracking-tight"
              style={{ color: COLORS.primary, fontWeight: 600 }}
            >
              Notre carte
            </h1>
            {distributor && (
              <div className="mt-3 flex items-center gap-2 text-sm" style={{ color: COLORS.muted }}>
                <MapPin className="w-4 h-4" />
                <span className="uppercase tracking-[0.18em]">{distributor.name || distributor.code}</span>
              </div>
            )}
            <p className="mt-2 max-w-xl text-sm sm:text-base" style={{ color: COLORS.muted }}>
              Cuisine thaïlandaise artisanale · Barquettes prêtes à réchauffer · Fabriquées à Conthey.
            </p>
          </div>
          {/* Lien discret vers la boutique — voir toute la gamme, PAS commander */}
          <Link
            to="/boutique"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity self-start sm:self-end"
            style={{ color: COLORS.gold }}
            data-testid="menu-shop-link"
          >
            Voir toute notre offre <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Bandeau info : 2 canaux d'achat clairement distingués (Phase 98s — clarification stratégique client) */}
      <div className="border-b" style={{ background: "#0A0A0A", borderColor: "rgba(193,160,99,0.15)" }} data-testid="menu-info-banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "rgba(136,160,102,0.12)", border: "1px solid rgba(136,160,102,0.35)" }}>
              <MapPin className="w-4 h-4" style={{ color: COLORS.basil }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] mb-0.5" style={{ color: COLORS.basil }}>
                Ici à la machine
              </div>
              <p className="text-xs sm:text-sm leading-snug" style={{ color: COLORS.primary }}>
                Prix affichés à côté du plat · disponible <strong>immédiatement</strong>.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "rgba(193,160,99,0.12)", border: "1px solid rgba(193,160,99,0.35)" }}>
              <ShoppingBag className="w-4 h-4" style={{ color: COLORS.gold }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] mb-0.5" style={{ color: COLORS.gold }}>
                Livraison isotherme
              </div>
              <p className="text-xs sm:text-sm leading-snug" style={{ color: COLORS.primary }}>
                Prix plus élevé · commande <strong>avant 10h la veille</strong> sur maison-thai.ch.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres catégorie */}
      {categories.length > 1 && (
        <div className="sticky top-0 z-10 backdrop-blur-md" style={{ background: "rgba(5,5,5,0.85)", borderBottom: "1px solid rgba(242,236,228,0.05)" }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`filter-${c}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] whitespace-nowrap transition-all rounded-full border ${activeCat === c ? "bg-white/10" : "hover:bg-white/5"}`}
                style={{
                  borderColor: activeCat === c ? COLORS.gold : "rgba(242,236,228,0.15)",
                  color: activeCat === c ? COLORS.gold : COLORS.primary,
                }}
              >
                {c === "Toutes" ? "Toutes" : (CATEGORY_TONE[c]?.label || c)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid tuiles */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        {loading && (
          <div className="py-24 flex items-center justify-center gap-3" style={{ color: COLORS.muted }}>
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Chargement de la carte…</span>
          </div>
        )}
        {error && !loading && (
          <div className="py-24 text-center" data-testid="menu-error">
            <p className="text-lg" style={{ color: COLORS.accent }}>{error}</p>
          </div>
        )}
        {!loading && !error && visible.length === 0 && (
          <div className="py-24 text-center" style={{ color: COLORS.muted }}>
            <p>Aucun plat disponible pour cette catégorie.</p>
          </div>
        )}
        {!loading && !error && visible.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="menu-grid">
            {visible.map((p, i) => (
              <MenuTile
                key={p.id || p.sku}
                product={p}
                featured={i === 0 && activeCat === "Toutes"}
                showAvailability={Boolean(distId)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer minimaliste */}
      <footer className="border-t px-4 sm:px-6 lg:px-10 py-8 sm:py-10 text-center text-xs" style={{ borderColor: "rgba(242,236,228,0.05)", color: COLORS.muted }}>
        <p className="uppercase tracking-[0.2em]">Maison Thaï Conthey · maison-thai.ch · +41 27 346 07 75</p>
        <p className="mt-2 text-[10px]">© {new Date().getFullYear()} — Fabrication artisanale en Valais · TVA 2,6% incluse</p>
      </footer>
    </div>
  );
}


function MenuTile({ product, featured, showAvailability }) {
  const photo = resolvePhoto(product.photo_url, 900) || resolvePhoto(product.photo_packaging_url, 900);
  const priceMachine = product.price_direct;
  const priceDelivery = product.price_online;
  const spice = Number(product.spice_level || 0);
  const catTone = CATEGORY_TONE[product.category] || CATEGORY_TONE["Général"];

  // Phase 98t — Dispo temps réel sur la machine + suggestion "ailleurs" si épuisé
  const qtyHere = Number(product.qty_available_here || 0);
  const others = product.available_at_others || [];
  const isOutOfStock = showAvailability && qtyHere === 0;
  const isLowStock = showAvailability && qtyHere > 0 && qtyHere <= 3;

  return (
    <div
      className={`group relative block overflow-hidden ${featured ? "sm:col-span-2 lg:col-span-2 aspect-[16/9]" : "aspect-[4/5]"}`}
      style={{ background: catTone.bg, border: "1px solid rgba(242,236,228,0.08)" }}
      data-testid={`menu-tile-${product.sku}`}
    >
      {/* Photo full-bleed */}
      {photo ? (
        <img
          src={photo}
          alt={product.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${isOutOfStock ? "grayscale opacity-40" : ""}`}
          onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ color: COLORS.muted }}>
          <span className="text-xs uppercase tracking-widest">Photo à venir</span>
        </div>
      )}

      {/* Overlay dégradé bottom pour lisibilité texte */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{
        background: "linear-gradient(to top, rgba(5,5,5,0.95) 20%, rgba(5,5,5,0) 100%)"
      }} />

      {/* Badges top-left : catégorie + spice + dispo */}
      <div className="absolute top-3 left-3 right-3 flex justify-between items-start gap-2">
        <div className="flex gap-1.5 flex-wrap">
          <span className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] backdrop-blur-md rounded-sm"
                style={{ background: "rgba(5,5,5,0.6)", color: COLORS.gold, border: "1px solid rgba(193,160,99,0.3)" }}>
            {catTone.label}
          </span>
          {/* Badge de dispo (uniquement si on est sur la vue par machine) */}
          {showAvailability && (
            <StockBadge qty={qtyHere} isOut={isOutOfStock} isLow={isLowStock} />
          )}
        </div>
        {spice > 0 && (
          <div className="flex gap-0.5 backdrop-blur-md rounded-sm px-1.5 py-1"
               style={{ background: "rgba(5,5,5,0.6)" }}
               title={`${spice}/3 piquant`}
          >
            {Array.from({ length: Math.min(spice, 3) }).map((_, i) => (
              <Flame key={i} className="w-3 h-3" style={{ color: COLORS.accent }} fill={COLORS.accent} />
            ))}
          </div>
        )}
      </div>

      {/* Contenu bottom : nom + description + 2 prix + suggestion épuisé */}
      <div className={`absolute inset-x-0 bottom-0 p-4 sm:p-5 ${featured ? "sm:p-6" : ""}`}>
        <h3 className={`font-heading font-medium leading-tight mb-1 tracking-tight ${featured ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"}`}
            style={{ color: COLORS.primary }}>
          {product.name}
        </h3>
        {product.short_description && !isOutOfStock && (
          <p className={`hidden sm:block mb-2 line-clamp-2 leading-snug ${featured ? "text-sm" : "text-xs"}`}
             style={{ color: COLORS.muted }}>
            {product.short_description}
          </p>
        )}

        {/* Suggestion "dispo ailleurs" si épuisé ici */}
        {isOutOfStock && others.length > 0 && (
          <div className="mb-2 flex items-start gap-1.5 text-xs" data-testid={`available-others-${product.sku}`}>
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" style={{ color: COLORS.gold }} />
            <div className="leading-snug">
              <span style={{ color: COLORS.gold }}>Dispo à </span>
              {others.map((o, idx) => (
                <React.Fragment key={o.distributor_id}>
                  <Link
                    to={`/carte/${o.distributor_id}`}
                    className="underline hover:no-underline font-medium"
                    style={{ color: COLORS.primary }}
                  >
                    {o.name.replace(/^Distributeur (de |d')/i, "")}
                  </Link>
                  {idx < others.length - 1 && <span style={{ color: COLORS.muted }}>, </span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {isOutOfStock && others.length === 0 && (
          <div className="mb-2 text-xs" style={{ color: COLORS.muted }}>
            Actuellement épuisé sur l'ensemble du réseau.
          </div>
        )}

        <div className="flex items-end justify-between gap-3 mt-2 flex-wrap">
          <div className="flex items-center gap-2 text-[11px]" style={{ color: COLORS.muted }}>
            {product.net_weight && (
              <span>{product.net_weight}{product.net_weight_unit || "g"}</span>
            )}
            {product.allergens?.length > 0 && (
              <span className="uppercase tracking-[0.1em]" title={product.allergens.join(", ")}>
                • {product.allergens.slice(0, 2).join("·")}{product.allergens.length > 2 ? "…" : ""}
              </span>
            )}
          </div>
          {/* Prix : 2 canaux (machine / livraison) */}
          <div className="flex items-end gap-3 flex-wrap justify-end">
            {priceMachine != null && (
              <div className="flex flex-col items-end leading-none">
                <span className="text-[9px] uppercase tracking-[0.16em]" style={{ color: COLORS.basil }}>Ici</span>
                <div className="flex items-baseline gap-0.5 mt-0.5" style={{ color: COLORS.primary, opacity: isOutOfStock ? 0.45 : 1 }}>
                  <span className={`font-mono font-semibold ${featured ? "text-xl" : "text-lg"}`}>
                    {Number(priceMachine).toFixed(2)}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest">CHF</span>
                </div>
              </div>
            )}
            {priceDelivery != null && priceDelivery !== priceMachine && (
              <div className="flex flex-col items-end leading-none">
                <span className="text-[9px] uppercase tracking-[0.16em]" style={{ color: COLORS.gold }}>Livraison</span>
                <div className="flex items-baseline gap-0.5 mt-0.5" style={{ color: COLORS.gold }}>
                  <span className={`font-mono ${featured ? "text-xl" : "text-lg"}`}>
                    {Number(priceDelivery).toFixed(2)}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest">CHF</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function StockBadge({ qty, isOut, isLow }) {
  if (isOut) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.14em] backdrop-blur-md rounded-sm"
        style={{ background: "rgba(192,92,71,0.15)", color: "#E88A76", border: "1px solid rgba(192,92,71,0.4)" }}
        data-testid="stock-badge-out"
      >
        <XCircle className="w-3 h-3" /> Épuisé ici
      </span>
    );
  }
  if (isLow) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.14em] backdrop-blur-md rounded-sm"
        style={{ background: "rgba(193,160,99,0.15)", color: "#D9BE8A", border: "1px solid rgba(193,160,99,0.4)" }}
        data-testid="stock-badge-low"
      >
        <AlertCircle className="w-3 h-3" /> {qty} restant{qty > 1 ? "s" : ""}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.14em] backdrop-blur-md rounded-sm"
      style={{ background: "rgba(136,160,102,0.15)", color: "#A8C48A", border: "1px solid rgba(136,160,102,0.4)" }}
      data-testid="stock-badge-ok"
    >
      <CheckCircle2 className="w-3 h-3" /> {qty} en stock
    </span>
  );
}
